import React, { useEffect, useState, useRef } from "react";
import { fetchCategories } from "../../services/HomePage/ShopByCategoryApi";
import MedicineByCategory from "./MedicineByCategory";
import "bootstrap/dist/css/bootstrap.min.css";

// ✅ ICONS
import { FaChevronLeft, FaChevronRight, FaFire, FaTag } from "react-icons/fa";

export default function ShopByCategory() {
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [activeDot, setActiveDot] = useState(0);

  // ✅ SCROLL REF
  const scrollRef = useRef();

  useEffect(() => {
    const load = async () => {
      const data = await fetchCategories();
      setCategories(data);
    };
    load();
  }, []);

  // ✅ CHECK SCROLL POSITION
  const checkScrollButtons = () => {
    const container = scrollRef.current;
    if (container) {
      const leftScroll = container.scrollLeft;
      const maxScroll = container.scrollWidth - container.clientWidth;
      
      setShowLeftArrow(leftScroll > 10);
      setShowRightArrow(leftScroll < maxScroll - 10);
      
      // ✅ UPDATE ACTIVE DOT BASED ON SCROLL POSITION
      const itemWidth = 150; // 130px width + 20px gap
      const visibleItems = Math.floor(container.clientWidth / itemWidth);
      const currentIndex = Math.round(leftScroll / itemWidth);
      const maxIndex = Math.max(0, categories.length - visibleItems);
      setActiveDot(Math.min(currentIndex, maxIndex));
    }
  };

  // ✅ SCROLL FUNCTIONS
  const scrollLeft = () => {
    const container = scrollRef.current;
    if (container) {
      container.scrollBy({ left: -300, behavior: "smooth" });
      setTimeout(checkScrollButtons, 500);
    }
  };

  const scrollRight = () => {
    const container = scrollRef.current;
    if (container) {
      container.scrollBy({ left: 300, behavior: "smooth" });
      setTimeout(checkScrollButtons, 500);
    }
  };

  // ✅ SCROLL TO SPECIFIC DOT
  const scrollToDot = (index) => {
    const container = scrollRef.current;
    if (container) {
      const itemWidth = 150; // 130px width + 20px gap
      const scrollPosition = index * itemWidth;
      container.scrollTo({ left: scrollPosition, behavior: "smooth" });
      setActiveDot(index);
      setTimeout(checkScrollButtons, 500);
    }
  };

  const handleImageError = (catId) => {
    setImageErrors(prev => ({ ...prev, [catId]: true }));
  };

  // ✅ SCROLL EVENT LISTENER
  useEffect(() => {
    const container = scrollRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollButtons);
      // Initial check
      setTimeout(checkScrollButtons, 100);
      
      return () => {
        container.removeEventListener('scroll', checkScrollButtons);
      };
    }
  }, [categories]);

  // ✅ WINDOW RESIZE HANDLER
  useEffect(() => {
    window.addEventListener('resize', checkScrollButtons);
    return () => {
      window.removeEventListener('resize', checkScrollButtons);
    };
  }, []);

  // ✅ CALCULATE TOTAL DOTS
  const getTotalDots = () => {
    const container = scrollRef.current;
    if (container && categories.length > 0) {
      const itemWidth = 150; // 130px width + 20px gap
      const visibleItems = Math.floor(container.clientWidth / itemWidth);
      return Math.max(1, categories.length - visibleItems + 1);
    }
    return Math.min(5, categories.length);
  };

  const totalDots = getTotalDots();

  return (
    <div className="px-4 md:px-8 py-6 bg-gradient-to-br from-gray-50 via-white to-red-50/30 rounded-xl shadow-lg mt-3">
      
      {/* Header Section */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-1.5 rounded-full shadow-md mb-3">
          <FaFire size={12} className="text-yellow-400" />
          <span className="text-xs font-bold tracking-wide">SHOP BY CATEGORY</span>
          <FaTag size={12} className="text-yellow-400" />
        </div>
        
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold">
          <span className="bg-gradient-to-r from-gray-800 to-gray-900 bg-clip-text text-transparent">
            Browse
          </span>{" "}
          <span className="bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">
            Health Categories
          </span>
        </h2>
        
        <p className="text-gray-500 text-sm mt-2 max-w-md mx-auto">
          Find medicines and healthcare products by category
        </p>
        
        {/* ✅ ACTIVE DOTS - NOW CLICKABLE */}
        <div className="flex justify-center gap-2 mt-4">
          {totalDots > 0 && Array.from({ length: Math.min(totalDots, 5) }).map((_, index) => (
            <button
              key={index}
              onClick={() => scrollToDot(index)}
              className={`transition-all duration-300 rounded-full ${
                index === activeDot
                  ? 'w-6 h-1.5 bg-gradient-to-r from-red-500 to-red-600'
                  : 'w-1.5 h-1.5 bg-red-300 hover:bg-red-400'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Scroll Container with Navigation Buttons */}
      <div className="relative group">
        {/* Left Navigation Button */}
        {showLeftArrow && (
          <button
            onClick={scrollLeft}
            className="absolute -left-3 md:-left-4 top-1/2 -translate-y-1/2 z-20 
            bg-white hover:bg-red-500 text-red-500 hover:text-white 
            shadow-lg p-2.5 rounded-full transition-all duration-300 hover:scale-110
            focus:outline-none"
          >
            <FaChevronLeft size={14} />
          </button>
        )}

        {/* Right Navigation Button */}
        {showRightArrow && (
          <button
            onClick={scrollRight}
            className="absolute -right-3 md:-right-4 top-1/2 -translate-y-1/2 z-20 
            bg-white hover:bg-red-500 text-red-500 hover:text-white 
            shadow-lg p-2.5 rounded-full transition-all duration-300 hover:scale-110
            focus:outline-none"
          >
            <FaChevronRight size={14} />
          </button>
        )}

        {/* Scroll Container */}
        <div
          ref={scrollRef}
          className="flex overflow-x-auto scroll-smooth"
          style={{
            gap: "20px",
            padding: "10px 40px",
            scrollbarWidth: "thin",
            msOverflowStyle: "auto",
          }}
        >
          {categories.map((cat, index) => (
            <div
              key={cat.id}
              className="group/card flex-shrink-0 cursor-pointer transform transition-all duration-300 hover:-translate-y-2"
              style={{ width: "130px" }}
              onClick={() => setSelectedCategoryId(cat.id)}
            >
              <div className="relative bg-white rounded-xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300">
                
                {/* Image Container */}
                <div className="relative h-36 overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
                  {!imageErrors[cat.id] && cat.catogary_Image ? (
                    <img
                      src={`https://admintest.medrphalabs.com/${cat.catogary_Image}`}
                      alt={cat.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover/card:scale-110"
                      onError={() => handleImageError(cat.id)}
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <FaTag size={40} className="text-gray-300" />
                    </div>
                  )}
                  
                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent 
                  opacity-0 group-hover/card:opacity-100 transition-opacity duration-300" />
                  
                  {/* Offer Badge */}
                  <div className="absolute top-2 left-2 z-10">
                    <div className="bg-gradient-to-r from-red-500 to-red-600 text-white text-[9px] font-bold px-2 py-0.5 rounded-full shadow-md">
                      {index % 2 === 0 ? "HOT DEAL" : "NEW"}
                    </div>
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-3 text-center">
                  <h3 className="text-sm font-bold text-gray-800 group-hover/card:text-red-600 transition-colors duration-300 line-clamp-2 min-h-[40px]">
                    {cat.name}
                  </h3>
                  
                  {/* View link on hover */}
                  <div className="flex items-center justify-center gap-1 mt-2 opacity-0 group-hover/card:opacity-100 transition-all duration-300 transform translate-y-1 group-hover/card:translate-y-0">
                    <span className="text-[10px] font-semibold text-red-500">Shop Now</span>
                    <FaChevronRight size={8} className="text-red-500" />
                  </div>
                </div>
                
                {/* Bottom Border Animation */}
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-red-500 to-transparent 
                transform scale-x-0 group-hover/card:scale-x-100 transition-transform duration-500" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Trust Badges */}
      {categories.length > 0 && (
        <div className="mt-8 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="flex items-center justify-center gap-2 text-xs text-gray-600 bg-white/50 rounded-lg p-2">
              <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 text-[10px] font-bold">✓</span>
              </div>
              <span>1000+ Categories</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-xs text-gray-600 bg-white/50 rounded-lg p-2">
              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 text-[10px] font-bold">⚡</span>
              </div>
              <span>Fast Delivery</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-xs text-gray-600 bg-white/50 rounded-lg p-2">
              <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-purple-600 text-[10px] font-bold">🏆</span>
              </div>
              <span>Genuine Products</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-xs text-gray-600 bg-white/50 rounded-lg p-2">
              <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
                <span className="text-red-600 text-[10px] font-bold">❤️</span>
              </div>
              <span>Best Prices</span>
            </div>
          </div>
        </div>
      )}

      {/* POPUP */}
      {selectedCategoryId && (
        <MedicineByCategory
          categoryId={selectedCategoryId}
          onClose={() => setSelectedCategoryId(null)}
        />
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.05); }
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        
        /* Custom scrollbar styling */
        .overflow-x-auto::-webkit-scrollbar {
          height: 6px;
        }
        
        .overflow-x-auto::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        
        .overflow-x-auto::-webkit-scrollbar-thumb {
          background: linear-gradient(90deg, #ef4444, #dc2626);
          border-radius: 10px;
        }
        
        .overflow-x-auto::-webkit-scrollbar-thumb:hover {
          background: #dc2626;
        }
      `}</style>
    </div>
  );
}